import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Flame, CheckCircle2, Circle, Target, ArrowRight, TrendingUp, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import WorkoutDetailCard from "@/components/WorkoutDetailCard";
import IntakeFormModal from "@/components/IntakeFormModal";

export default function ClientHomePage() {
    const [data, setData] = useState(null);
    const [intakeOpen, setIntakeOpen] = useState(false);
    const [celebrate, setCelebrate] = useState(false);
    const [prevStreak, setPrevStreak] = useState(null);

    const load = async () => {
        const { data } = await api.get("/me/client-home");
        // Detect streak milestone bumps for celebration
        if (data?.streak && prevStreak !== null && data.streak.current > prevStreak) {
            const milestones = [3, 7, 14, 21, 30, 60, 100];
            if (milestones.includes(data.streak.current)) setCelebrate(data.streak.current);
        }
        setPrevStreak(data?.streak?.current ?? null);
        setData(data);
        if (data && data.intake_completed === false) setIntakeOpen(true);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const complete = async (wid) => {
        await api.post(`/me/workouts/${wid}/complete`);
        load();
    };

    const toggleHabit = async (h) => {
        if (h.done_today) await api.delete("/habit-logs", { params: { habit_id: h.id } });
        else await api.post("/habit-logs", { habit_id: h.id });
        load();
    };

    if (!data) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

    const today = data.today || [];
    const week = data.this_week || [];
    const doneThisWeek = week.filter((w) => w.completed).length;
    const streak = data.streak || { current: 0, best: 0, consistency_30d: 0, active_today: false };
    const mission = data.mission;
    const firstName = data.client?.name?.split(" ")[0] || "athlete";

    // Greeting that varies by time of day
    const hour = new Date().getHours();
    const greet = hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening";

    return (
        <div className="p-5 md:p-8 pb-24 md:pb-8 space-y-6 md:space-y-8" data-testid="client-home-page">
            {/* Header */}
            <div>
                <p className="label-tiny mb-2">/ {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
                <h1 className="font-serif text-4xl md:text-6xl">{greet}, {firstName}.</h1>
                {streak.current >= 3 && (
                    <p className="text-[var(--gold-bright)] mt-3 text-sm font-mono tracking-wider">
                        🔥 {streak.current}-day streak — don't break the chain.
                    </p>
                )}
                {streak.current === 0 && doneThisWeek === 0 && (
                    <p className="text-[var(--text-muted)] mt-3 text-sm">Today's the day. One session to start the chain.</p>
                )}
                {data.client?.goal && (
                    <p className="text-[var(--text-muted)] mt-2 text-sm">Goal · <span className="text-[var(--text)]">{data.client.goal}</span> · with {data.coach_name}</p>
                )}
            </div>

            {/* Daily Mission — the hero card */}
            {mission && (
                <div className={`relative border-2 ${mission.type === "rest" ? "border-[var(--border-bright)]" : "border-[var(--gold)]"} bg-[var(--surface)] p-6 md:p-7 overflow-hidden`} data-testid="daily-mission">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-[var(--gold)] opacity-[0.04] rounded-full blur-3xl" />
                    <div className="relative">
                        <div className="flex items-center gap-2 mb-3">
                            {mission.type === "workout" ? <Zap size={14} className="text-[var(--gold-bright)]" />
                                : mission.type === "habit" ? <Target size={14} className="text-[var(--gold-bright)]" />
                                : <CheckCircle2 size={14} className="text-[var(--gold-bright)]" />}
                            <p className="label-tiny text-[var(--gold-bright)]">/ Today's mission</p>
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl mb-2 leading-tight">{mission.title}</h2>
                        <p className="text-sm text-[var(--text-muted)] mb-5">{mission.subtitle}</p>
                        {mission.type === "habit" ? (
                            <button onClick={() => toggleHabit({ id: mission.habit_id, done_today: false })}
                                className="btn-primary inline-flex items-center gap-2" data-testid="mission-cta">
                                <CheckCircle2 size={14} /> {mission.cta_label}
                            </button>
                        ) : mission.type === "workout" ? (
                            <a href="#today-session" className="btn-primary inline-flex items-center gap-2" data-testid="mission-cta">
                                {mission.cta_label} <ArrowRight size={14} />
                            </a>
                        ) : (
                            <Link to={mission.cta_href} className="btn-ghost inline-flex items-center gap-2" data-testid="mission-cta">
                                {mission.cta_label} <ArrowRight size={14} />
                            </Link>
                        )}
                    </div>
                </div>
            )}

            {/* Streak / Consistency / Week */}
            <div className="grid grid-cols-3 gap-px bg-[var(--border)]" data-testid="streak-stats">
                <div className="bg-[var(--surface)] p-4 md:p-5 text-center md:text-left" data-testid="stat-streak">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                        <Flame size={14} className={streak.current > 0 ? "text-[var(--gold-bright)]" : "text-[var(--text-faint)]"} />
                        <p className="label-tiny">Streak</p>
                    </div>
                    <p className="font-display text-4xl md:text-6xl">{streak.current}</p>
                    <p className="text-[10px] md:text-xs text-[var(--text-muted)] font-mono mt-1">days · best {streak.best}</p>
                </div>
                <div className="bg-[var(--surface)] p-4 md:p-5 text-center md:text-left" data-testid="stat-consistency">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                        <TrendingUp size={14} className="text-[var(--gold-bright)]" />
                        <p className="label-tiny">30-day</p>
                    </div>
                    <p className="font-display text-4xl md:text-6xl">{streak.consistency_30d}<span className="text-[var(--text-faint)] text-xl md:text-3xl">%</span></p>
                    <p className="text-[10px] md:text-xs text-[var(--text-muted)] font-mono mt-1">consistency</p>
                </div>
                <div className="bg-[var(--surface)] p-4 md:p-5 text-center md:text-left" data-testid="stat-week-done">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                        <CheckCircle2 size={14} className="text-[var(--gold-bright)]" />
                        <p className="label-tiny">This week</p>
                    </div>
                    <p className="font-display text-4xl md:text-6xl">{doneThisWeek}<span className="text-[var(--text-faint)] text-xl md:text-3xl">/{week.length || 0}</span></p>
                    <p className="text-[10px] md:text-xs text-[var(--text-muted)] font-mono mt-1">sessions done</p>
                </div>
            </div>

            {/* Today's session */}
            <div id="today-session" className="border border-[var(--border)] bg-[var(--surface)]">
                <div className="p-5 border-b border-[var(--border)]">
                    <p className="label-tiny mb-1">/ Today</p>
                    <h3 className="font-serif text-3xl">Your session</h3>
                </div>
                {today.length === 0 ? (
                    <p className="p-6 text-[var(--text-muted)] text-sm">Rest day — recover hard. See you tomorrow.</p>
                ) : today.map((w) => (
                    <WorkoutDetailCard key={w.id} workout={w} onComplete={complete} />
                ))}
            </div>

            {/* Habits */}
            {data.habits.length > 0 && (
                <div className="border border-[var(--border)] bg-[var(--surface)]">
                    <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
                        <div>
                            <p className="label-tiny mb-1">/ Daily habits</p>
                            <h3 className="font-serif text-3xl">Stack the wins</h3>
                        </div>
                        <Link to="/client/habits" className="text-sm text-[var(--gold)]">All habits →</Link>
                    </div>
                    <div className="divide-y divide-[var(--border)]">
                        {data.habits.map((h) => (
                            <button key={h.id} onClick={() => toggleHabit(h)} data-testid={`habit-toggle-${h.id}`}
                                className="w-full p-4 text-left flex items-center justify-between hover:bg-[var(--surface-hover)]">
                                <div className="flex items-center gap-3">
                                    {h.done_today ? <CheckCircle2 size={20} className="text-[var(--gold-bright)]" /> : <Circle size={20} className="text-[var(--text-faint)]" />}
                                    <div>
                                        <p className={`font-medium ${h.done_today ? "line-through text-[var(--text-muted)]" : ""}`}>{h.name}</p>
                                        {h.target && <p className="text-xs text-[var(--text-muted)]">target: {h.target}</p>}
                                    </div>
                                </div>
                                <span className="tag-pill">{h.frequency}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Streak milestone celebration overlay */}
            {celebrate && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6" onClick={() => setCelebrate(false)} data-testid="streak-celebration">
                    <div className="text-center max-w-md">
                        <Flame size={80} className="text-[var(--gold-bright)] mx-auto mb-6 animate-pulse" />
                        <p className="font-display text-7xl md:text-8xl mb-3 gold-gradient-text">{celebrate}</p>
                        <p className="font-serif text-3xl md:text-4xl mb-2">days straight.</p>
                        <p className="text-sm text-[var(--text-muted)] mb-8">This is exactly how transformations happen. Keep going.</p>
                        <button onClick={() => setCelebrate(false)} className="btn-primary">Lock in</button>
                    </div>
                </div>
            )}

            <IntakeFormModal open={intakeOpen} onClose={() => setIntakeOpen(false)} onDone={load} />
        </div>
    );
}
